import React from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import WarningIcon from '@material-ui/icons/Warning';
import NotInterestedIcon from '@material-ui/icons/NotInterested';
import { StepperNode, formatRenewalTimestamp } from '../../service/RenewalStatusService';
import './RenewalStatusStepper.css';

const STAGE_DESCRIPTIONS: Record<string, string> = {
  TRIGGERED: 'The renewal request was accepted.',
  INITIATED: 'The account was picked up for renewal.',
  IN_PROGRESS: 'Subscriptions on this account are being processed.',
  COMPLETED: 'The renewal has finished, successfully or not.',
  SUPERSEDED: 'Another renewal attempt on this account took over.'
};

interface RenewalStatusStepperProps {
  nodes: StepperNode[];
}

// This is a purpose-built timeline rather than MUI's <Stepper>: MUI's Step/StepContent
// are built for a wizard reveal/collapse (only the active step's content is visible) and
// force a fixed set of injected props onto every child, which fights a persistent
// history view like this one. A plain flex layout gives full control over the
// always-visible badge/connector colors this design calls for.
function badgeIcon(status: StepperNode['status']): React.ReactElement {
  switch (status) {
    case 'active':
      return <CircularProgress size={14} className='renewal-stepper-badge-spinner' />;
    case 'complete':
      return <CheckIcon className='renewal-stepper-badge-icon' />;
    case 'failed':
      return <CloseIcon className='renewal-stepper-badge-icon' />;
    case 'superseded':
      return <NotInterestedIcon className='renewal-stepper-badge-icon' />;
  }
}

// Full date+time for every node — a time-only label (e.g. "08:39:07Z") is ambiguous
// without a date once the account has any history spanning more than a few minutes.
function stageTimestamp(recordedAt?: string | null): string {
  const iso = formatRenewalTimestamp(recordedAt);
  return iso ? iso.replace('T', ' ') : '';
}

function completedAtLabel(recordedAt?: string | null): string {
  const timestamp = stageTimestamp(recordedAt);
  return timestamp ? `Completed at ${timestamp}` : '';
}

function RenewalStatusStepper(props: Readonly<RenewalStatusStepperProps>): React.ReactElement {
  const { nodes } = props;

  return (
    <div className='renewal-stepper'>
      {nodes.map((node, index) => {
        const isLast = index === nodes.length - 1;
        const nextStatus = !isLast ? nodes[index + 1].status : undefined;
        return (
          <div key={node.key} className='renewal-stepper-row'>
            <div className='renewal-stepper-rail'>
              <span className={`renewal-stepper-badge renewal-stepper-badge--${node.status}`}>
                {badgeIcon(node.status)}
              </span>
              {!isLast && <span className={`renewal-stepper-connector renewal-stepper-connector--${nextStatus}`} />}
            </div>
            <div className='renewal-stepper-body'>
              <div className='renewal-stepper-label-row'>
                <span className='renewal-stepper-label'>{node.label}</span>
                {STAGE_DESCRIPTIONS[node.key] && (
                  <Tooltip title={STAGE_DESCRIPTIONS[node.key]}>
                    <InfoOutlinedIcon fontSize='small' className='renewal-stepper-info-icon' />
                  </Tooltip>
                )}
              </div>
              <div className='renewal-stepper-timestamp'>
                {node.key === 'COMPLETED' ? completedAtLabel(node.recordedAt) : stageTimestamp(node.recordedAt)}
              </div>
              {(node.status === 'failed' || node.status === 'superseded') && node.message && (
                <div className={`renewal-stepper-message renewal-stepper-message-${node.status}`}>
                  <WarningIcon className='renewal-stepper-message-icon' />
                  <span>
                    {node.message}
                    {node.reasonCode ? ` (${node.reasonCode})` : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default RenewalStatusStepper;
